<script setup lang="ts">
import { computed, inject, onMounted, ref } from "vue";
import {
  type FormResponseState,
  formResponseInjectKey,
} from "~/NewFormBuilder/composables/useFormResponse";

import {
  type ComplianceItemFormElement,
  FormDefinition,
  FormElementBase,
  FormResponse,
  ItemDefinition,
} from "~/api";
import { FormDisplayMode } from "~/NewFormBuilder/types/FormDisplayMode";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import type { DefaultFormComponentProps } from "../FormComponentRenderer.vue";

import { apiClient } from "~/utils/apiClient";
import { emptyGuid } from "~/utils/constants";
import { showError } from "~/utils/handleAPIError";

import FormRendererInner from "~/NewFormBuilder/components/Renderer/FormRendererInner.vue";

const props =
  defineProps<DefaultFormComponentProps<ComplianceItemFormElement>>();

const formResponseState = inject(
  formResponseInjectKey,
  // Cast the type since we know it is always provided. For more info see:
  // https://vuejs.org/guide/typescript/composition-api.html#typing-provide-inject
) as FormResponseState;

const { formResponse, updateNestedUnansweredQuestions } = formResponseState;

const myInfoStore = useMyInfoStore();

const elementProperties = computed(() => props.modelValue.properties);

const itemDefinition = ref<ItemDefinition>();

const complianceForm = ref<FormDefinition>();
const complianceFormResponse = ref<FormResponse>();

const hasSetupBasicRole = myInfoStore.hasSetupBasicRole;

const formDisplayMode = computed(() =>
  hasSetupBasicRole || props.isReadOnly
    ? props.displayMode
    : FormDisplayMode.READ_ONLY,
);

onMounted(async () => {
  if (!elementProperties.value.itemDefinitionGuid) {
    return;
  }

  const elementGuid = props.modelValue.guid;

  if (formResponse) {
    const nestedFormData = formResponse.answers.find(
      // find the answer that corresponds to this element
      (answer) =>
        answer.formElementGuid === elementGuid &&
        answer.repeatableGroupGuid === props.modelValue.repeaterGuid,
    )?.nestedFormResponse; // get the nested form response

    if (!nestedFormData) {
      await apiClient.formResponseAnswer
        .getAnswer(
          formResponse.formDefinitionGuid,
          formResponse.guid,
          elementGuid,
          props.modelValue.repeaterGuid ?? emptyGuid,
        )
        .then((answer) => {
          if (!answer.nestedFormResponse) {
            showError(
              `No nested form response found for element ${elementGuid} in form response ${formResponse.guid}`,
            );
            return;
          }
          complianceForm.value = answer.nestedFormResponse.formDefinition;
          complianceFormResponse.value = answer.nestedFormResponse.formResponse;
        })
        .catch(showError);
    } else {
      showError(
        `No nested form response found for element ${elementGuid} in form response ${formResponse.guid}`,
      );
      complianceForm.value = nestedFormData.formDefinition;
      complianceFormResponse.value = nestedFormData.formResponse;
    }
  } else {
    // get the item definition
    await apiClient.itemDefinition
      .getItemDefinition(
        elementProperties.value.itemDefinitionGuid,
        myInfoStore.currentSysOrgGuid,
      )
      .then((res) => {
        itemDefinition.value = res;
      })
      .catch(showError);

    if (!itemDefinition.value?.formDefinitionGuid) {
      showError(
        `Item definition ${elementProperties.value.itemDefinitionGuid} does not have a form definition`,
      );
      return;
    }

    // get the form
    await apiClient.formDefinition
      .getFormDefinition(itemDefinition.value.formDefinitionGuid)
      .then((res) => {
        complianceForm.value = res;
      })
      .catch(showError);

    return;
  }
});
</script>

<template id="form-ComplianceItem-component">
  <div
    v-if="complianceForm"
    :class="{
      ComplianceItemComponent: !complianceFormResponse,
      'response-viewer': complianceFormResponse,
      'read-only-mode': !hasSetupBasicRole && !props.isReadOnly,
    }"
  >
    <FormRendererInner
      :key="complianceFormResponse?.guid"
      v-model="complianceForm"
      :form-response="complianceFormResponse"
      :display-mode="formDisplayMode"
      :shared-answers="formResponseState.sharedAnswers"
      @update:unanswered-questions="
        ($event: FormElementBase[]) => {
          updateNestedUnansweredQuestions($event, complianceForm?.guid);
        }
      "
    />
  </div>
</template>

<style lang="less">
.ComplianceItemComponent {
  display: grid;
  flex-direction: row;
  gap: 10px;
  align-items: center;
  justify-content: space-between;

  padding: 10px;
  border-radius: var(--el-border-radius-base);
  border: 3px solid var(--el-color-warning-light-3);
}

.read-only-mode {
  border: 3px solid var(--el-color-info-light-3);

  .read-only-notice {
    margin-bottom: 10px;
  }
}

// TODO: these styles make a compliance item hard to distinguish when
// it is in a response viewer. it also introduces some issues in the repeater
// component as there are varying indentations/overlaps. need to explore how
// to improve this.
// .response-viewer {
//   border: none;

//   .formHost {
//     margin-left: -2em;
//     margin-top: -2em;
//     margin-bottom: -3em;
//   }

//   .formHost .form {
//     border: none;
//     width: 99.2%; // Need to find a better way to style this
//   }

//   .SectionComponent {
//     display: none;
//   }
// }
</style>
